'use client';

import React, { useEffect, useState } from 'react';

import {
  type AllProductsQueryParams,
  type ProductListItem,
} from '@/common/types/product.type';
import { selectVendorId, useVendorStore } from '@/store/use-vendor.store';
import { useInView } from 'react-intersection-observer';

import { useGetVendorCollectionProducts } from '@/common/apis/collection/v1/get-vendor-collection-products';
import EmptyState from '@/common/components/empty-state/empty-state.component';
import Skeleton from '@/common/components/skeleton';
import Text from '@/common/components/text/text.component';
import createUrlUtility from '@/common/utils/create-url.utility';

import Product from './product.component';

export interface Props {
  queryParams: AllProductsQueryParams;
  collectionId: number;
}

export default function Products({ queryParams, collectionId }: Props) {
  const { ref, inView } = useInView();
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const {
    sortDirection,
    sortCondition,
    pageSize,
    categoryList,
    isAvailable,
    isDiscounted,
    searchByProductName,
  } = queryParams;
  const vendorId = useVendorStore(selectVendorId);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isSuccess,
    isLoading,
    isFetchingNextPage,
  } = useGetVendorCollectionProducts({
    AssortmentId: collectionId,
    vendorId,
    searchByProductName,
    sortDirection,
    sortCondition,
    categoryList,
    isAvailable,
    isDiscounted,
    pageSize,
  });

  useEffect(() => {
    if (isSuccess && !isFetching) {
      setNewestProductItems();
    }
  }, [isSuccess, data, isFetching]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage]);

  const setNewestProductItems = () => {
    const productItems = data?.pages.flatMap((page: any) => page.data.products);
    productItems && setProducts(productItems);
  };

  return (
    <div className="relative h-full overflow-auto ">
      {products.length ? (
        <>
          <div className="mb-3 flex items-center justify-between text-xs text-secondary-300 lg:mb-4">
            <Text className="text-sm font-semibold">تعداد محصولات</Text>
            <h2 className="text-sm font-normal">
              {`${data?.pages[0].data.productCount ?? ''}`} محصول
            </h2>
          </div>
          <div className="grid grid-cols-2 justify-start gap-4 md:gap-[27px] lg:grid-cols-5">
            {products.map((item) => (
              <div
                className="mb-3 box-border flex  cursor-pointer flex-col overflow-hidden border-none"
                key={item?.id}
                ref={ref}
              >
                <Product key={item?.id} itemDetail={item} />
              </div>
            ))}
          </div>
          {isFetchingNextPage && (
            <div className="mt-6 grid grid-cols-2 gap-x-4  gap-y-8 bg-silver-100 px-4 pb-4 lg:mt-12 lg:flex lg:flex-wrap lg:justify-between lg:gap-x-4 lg:px-0">
              <Skeleton type="product-item" repeat={5} />
            </div>
          )}
        </>
      ) : (
        !products.length &&
        !isFetching && (
          <EmptyState
            message="محصولی یافت نشد!"
            iconSrc={createUrlUtility({
              params: 'static/images/svg/empty-state-product.svg',
            })}
            className="!static !my-4"
          />
        )
      )}
      {isLoading && (
        <div className="mt-6 grid grid-cols-2 gap-x-4  gap-y-8 bg-silver-100 px-4 pb-4 lg:mt-12 lg:flex lg:flex-wrap lg:justify-between lg:gap-x-4 lg:px-0">
          <Skeleton type="product-item" repeat={5} />
        </div>
      )}
    </div>
  );
}
